import React, { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Dialog, Typography } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CalendarEventSnackBar } from '../../components';
import {
  TEAM_DEFAULT_DETAIL_SECTION,
  TEAM_NAME_MAX_LENGTH,
  TEAM_SETTINGS_SECTION_ID_MAP,
  TeamDetailSection,
  TeamSettingsSection,
  TeamBlankStage,
  TeamCreateCard,
  TeamDayView,
  TeamEmptyHome,
  TeamErrorStateCard,
  TeamAcceptInviteModal,
  TeamBetaIntroModal,
  TeamInviteModal,
  TeamSharingSettings,
  TeamShell,
  TeamSideNav,
  TeamTasksView,
  TeamTopBar,
  getTeamInitials,
  isTeamDetailSection,
} from '../../components/Team';
import { buildIdLookup, getSharedCount, isTeamNotFoundError } from '../../components/Team/utils';
import {
  useAuthContext,
  useCalendarsMeta,
  useTeamContext,
  useTodoProjects,
} from '../../data-access';
import { useTeamCreateFlow, useTeamInviteFlow } from '../../hooks';

import { Path } from '../../routing';
import { clearPendingTeamInviteRedirect } from '../../routing/teamInviteRedirect';
import { trackEventMixpanel } from '../../utils';
import { safeLocalStorage } from '../../utils/safeLocalStorage';
import './TeamPage.scss';
import { AISuggestedEventSheetWrapper } from '../DashboardPage/CalendarView/EventSheet/AISuggestedEventSheetWrapper';

const LAST_SELECTED_TEAM_ID_STORAGE_KEY = 'lifestack_last_selected_team_id';
const TEAM_BETA_INTRO_STORAGE_KEY = 'lifestack_team_beta_intro_seen';

const TeamPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    teamId,
    teamView,
    inviteCode,
    '*': inviteCodeSplat,
  } = useParams<{
    teamId?: string;
    teamView?: string;
    inviteCode?: string;
    '*': string;
  }>();
  const { user } = useAuthContext();
  const {
    teams,
    selectedTeam,
    selectedMembership,
    selectedMembers,
    selectedBoards,
    activeTeamName,
    hasTeams,
    createTeamError,
    createTeamInviteError,
    teamInviteResolveError,
    acceptTeamInviteError,
    updateTeamError,
    leaveTeamError,
    teamMemberActionError,
    teamProjectActionError,
    isCreatingTeam,
    isCreatingTeamInvite,
    isResolvingTeamInvite,
    isAcceptingTeamInvite,
    isTeamsLoading,
    isTeamLoading,
    teamListError,
    teamDetailError,
    updateTeamShareSettingsError,
    isUpdatingTeam,
    isLeavingTeam,
    isDeletingTeam,
    isUpdatingTeamMemberRole,
    isRemovingTeamMember,
    isTransferringTeamOwnership,
    isUpdatingTeamShareSettings,
    isCreatingTeamBoard,
    isUpdatingTeamBoard,
    isDeletingTeamBoard,
    createTeam,
    createTeamInvite,
    resolveTeamInvite,
    acceptTeamInvite,
    updateTeamName,
    leaveTeam,
    deleteTeam,
    updateTeamMemberRole,
    removeTeamMember,
    transferTeamOwnership,
    updateTeamShareSettings,
    createTeamBoard,
    updateTeamBoard,
    deleteTeamBoard,
    clearCreateTeamError,
    clearCreateTeamInviteError,
    clearTeamInviteResolveError,
    clearAcceptTeamInviteError,
    clearUpdateTeamError,
    clearLeaveTeamError,
    clearTeamMemberActionError,
    clearTeamProjectActionError,
    clearUpdateTeamShareSettingsError,
  } = useTeamContext();

  const userId = user.userId || '';
  const { calendarsMeta, isLoading: isCalendarsLoading } = useCalendarsMeta({ userId });
  const { todoProjects, isLoading: isTaskBoardsLoading } = useTodoProjects({ userId });

  const [teamDropdownAnchorEl, setTeamDropdownAnchorEl] = useState<HTMLElement | null>(null);
  const [teamSearch, setTeamSearch] = useState('');
  const [activeSettingsSection, setActiveSettingsSection] = useState<TeamSettingsSection>('rename');
  const [excludedResolverTeamId, setExcludedResolverTeamId] = useState<string | null>(null);
  const [isTeamBetaIntroOpen, setIsTeamBetaIntroOpen] = useState(false);

  const isTeamDetailView = Boolean(teamId);
  const isTeamInviteRoute = location.pathname.startsWith(`${Path.TEAM}/invite`);
  const inviteCodeFromSplat = isTeamInviteRoute ? inviteCodeSplat?.split('/')[0] : undefined;
  const activeDetailSection: TeamDetailSection = isTeamDetailSection(teamView)
    ? teamView
    : TEAM_DEFAULT_DETAIL_SECTION;
  const isTeamDropdownOpen = Boolean(teamDropdownAnchorEl);
  const resolvedTeamName =
    activeTeamName || selectedTeam?.name || teams[0]?.team.name || t('team.common.team');
  const teamInitials = useMemo(() => getTeamInitials(resolvedTeamName), [resolvedTeamName]);
  const teamMembers = useMemo(() => {
    if (selectedMembers.length) {
      return selectedMembers;
    }

    return selectedMembership ? [selectedMembership] : [];
  }, [selectedMembers, selectedMembership]);
  const activeTeamId = selectedTeam?.teamId || teamId;
  const isTeamExitActionLoading = isLeavingTeam || isDeletingTeam;
  const activeTeamListItem = useMemo(
    () => teams.find(({ team }) => team.teamId === activeTeamId),
    [activeTeamId, teams]
  );
  const activeTeamRole =
    selectedMembership?.role || activeTeamListItem?.membership.role || 'member';
  const trackTeamEvent = (action: string, params?: Record<string, unknown>) => {
    trackEventMixpanel(action, {
      team_id: activeTeamId,
      team_role: activeTeamRole,
      team_view: activeDetailSection,
      ...params,
    });
  };
  const canRenameTeam = activeTeamRole === 'owner' || activeTeamRole === 'admin';
  const canManageTeamProjects = canRenameTeam;
  const defaultSettingsSection: TeamSettingsSection = canRenameTeam ? 'rename' : 'sharedCalendar';
  const resolvedSharedCalendarIds =
    selectedMembership?.sharedCalendarIds ?? activeTeamListItem?.membership.sharedCalendarIds ?? [];
  const resolvedSharedTaskBoardIds =
    selectedMembership?.sharedTaskBoardIds ??
    activeTeamListItem?.membership.sharedTaskBoardIds ??
    [];
  const availableCalendarIdsLookup = useMemo(() => {
    const calendarIds = calendarsMeta
      .filter((calendar) => Boolean(calendar.calendarId))
      .map((calendar) => calendar.calendarId);

    return buildIdLookup(calendarIds);
  }, [calendarsMeta]);
  const availableTaskBoardIdsLookup = useMemo(() => {
    const taskBoardIds = todoProjects
      .filter((board) => Boolean(board.todoTaskId))
      .map((board) => board.todoTaskId);

    return buildIdLookup(taskBoardIds);
  }, [todoProjects]);
  const sharedCalendarCount = useMemo(() => {
    return getSharedCount(
      resolvedSharedCalendarIds,
      availableCalendarIdsLookup,
      isCalendarsLoading
    );
  }, [resolvedSharedCalendarIds, availableCalendarIdsLookup, isCalendarsLoading]);
  const sharedTaskBoardCount = useMemo(() => {
    return getSharedCount(
      resolvedSharedTaskBoardIds,
      availableTaskBoardIdsLookup,
      isTaskBoardsLoading
    );
  }, [resolvedSharedTaskBoardIds, availableTaskBoardIdsLookup, isTaskBoardsLoading]);
  const shareEnergyEnabled =
    selectedMembership?.shareEnergy ?? activeTeamListItem?.membership.shareEnergy ?? false;
  const resolvedTeamHomeTeamId = useMemo(() => {
    const resolverTeams = excludedResolverTeamId
      ? teams.filter(({ team }) => team.teamId !== excludedResolverTeamId)
      : teams;
    const storedTeamId = safeLocalStorage.getItem(LAST_SELECTED_TEAM_ID_STORAGE_KEY);

    if (storedTeamId && resolverTeams.some(({ team }) => team.teamId === storedTeamId)) {
      return storedTeamId;
    }

    return resolverTeams[0]?.team.teamId || null;
  }, [excludedResolverTeamId, teams]);

  const {
    teamName,
    createError,
    isCreateDisabled,
    isCreateTeamModalOpen,
    handleTeamNameChange,
    handleCreateTeam,
    openCreateTeamModal,
    closeCreateTeamModal,
  } = useTeamCreateFlow({
    maxTeamNameLength: TEAM_NAME_MAX_LENGTH,
    isCreatingTeam,
    createTeamError,
    clearCreateTeamError,
    createTeam,
    onTeamCreated: (createdTeamId: string) => {
      trackEventMixpanel('team_created', {
        team_id: createdTeamId,
        source: 'team_page',
      });
      setExcludedResolverTeamId(null);
      safeLocalStorage.setItem(LAST_SELECTED_TEAM_ID_STORAGE_KEY, createdTeamId);
      navigate(`${Path.TEAM}/${createdTeamId}/${TEAM_DEFAULT_DETAIL_SECTION}`);
    },
  });

  const {
    activeInviteCode,
    isAcceptInviteModalOpen,
    inviteTeamName,
    resolvedInviteRole,
    resolvedInviteInviterName,
    resolvedInvitePreviewMembers,
    resolvedInviteMemberSummaryText,
    isViewerEligibleForInvite,
    inviteBlockedReason,
    inviteCalendarOptions,
    inviteTaskBoardOptions,
    normalizedInviteCalendarIds,
    normalizedInviteTaskBoardIds,
    isInviteShareEnergyEnabled,
    inviteEmail,
    createTeamInviteErrorMessage,
    generatedInviteUrl,
    isCreateTeamInviteModalOpen,
    isCopyingInviteLink,
    isInviteLinkCopied,
    isGenerateInviteDisabled,
    openCreateTeamInviteModal,
    closeCreateTeamInviteModal,
    handleInviteEmailChange,
    handleGenerateInviteLink,
    handleCopyInviteLink,
    handleToggleInviteCalendar,
    handleToggleInviteTaskBoard,
    handleToggleInviteEnergy,
    handleAcceptInvite,
    handleDeclineInvite,
  } = useTeamInviteFlow({
    teamId,
    inviteCodeParam: inviteCode || inviteCodeFromSplat,
    locationSearch: location.search,
    activeTeamId,
    calendarsMeta,
    todoProjects,
    isCalendarsLoading,
    isTaskBoardsLoading,
    availableCalendarIdsLookup,
    availableTaskBoardIdsLookup,
    createTeamInvite,
    isCreatingTeamInvite,
    createTeamInviteError,
    clearCreateTeamInviteError,
    resolveTeamInvite,
    clearTeamInviteResolveError,
    acceptTeamInvite,
    clearAcceptTeamInviteError,
    isAcceptingTeamInvite,
    onInviteAccepted: (acceptedTeamId: string) => {
      clearPendingTeamInviteRedirect();
      setExcludedResolverTeamId(null);
      safeLocalStorage.setItem(LAST_SELECTED_TEAM_ID_STORAGE_KEY, acceptedTeamId);
      navigate(`${Path.TEAM}/${acceptedTeamId}/${TEAM_DEFAULT_DETAIL_SECTION}`, { replace: true });
    },
    onInviteDeclined: () => {
      clearPendingTeamInviteRedirect();
      navigate(Path.TEAM, { replace: true });
    },
  });
  const shouldHoldTeamHomeForInvite = isTeamInviteRoute && Boolean(activeInviteCode);

  const filteredTeams = useMemo(() => {
    const normalizedSearch = teamSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return teams;
    }

    return teams.filter(({ team }) => team.name.toLowerCase().includes(normalizedSearch));
  }, [teamSearch, teams]);

  useEffect(() => {
    setTeamDropdownAnchorEl(null);
    setTeamSearch('');
    setActiveSettingsSection(defaultSettingsSection);
    clearUpdateTeamShareSettingsError();
    clearUpdateTeamError();
    clearLeaveTeamError();
    clearTeamMemberActionError();
    clearTeamProjectActionError();
  }, [
    teamId,
    clearLeaveTeamError,
    clearTeamMemberActionError,
    clearTeamProjectActionError,
    clearUpdateTeamError,
    clearUpdateTeamShareSettingsError,
    defaultSettingsSection,
  ]);

  useEffect(() => {
    if (
      !excludedResolverTeamId ||
      teams.some(({ team }) => team.teamId === excludedResolverTeamId)
    ) {
      return;
    }

    setExcludedResolverTeamId(null);
  }, [excludedResolverTeamId, teams]);

  useEffect(() => {
    if (
      (activeSettingsSection !== 'rename' || canRenameTeam) &&
      (activeSettingsSection !== 'projects' || canManageTeamProjects)
    ) {
      return;
    }

    setActiveSettingsSection('sharedCalendar');
  }, [activeSettingsSection, canManageTeamProjects, canRenameTeam]);

  useEffect(() => {
    if (!teamId) {
      return;
    }

    if (!teamView || !isTeamDetailSection(teamView)) {
      navigate(`${Path.TEAM}/${teamId}/${TEAM_DEFAULT_DETAIL_SECTION}`, { replace: true });
    }
  }, [teamId, teamView, navigate]);

  useEffect(() => {
    if (
      isTeamDetailView ||
      shouldHoldTeamHomeForInvite ||
      isTeamsLoading ||
      teamListError ||
      !hasTeams ||
      !resolvedTeamHomeTeamId
    ) {
      return;
    }

    safeLocalStorage.setItem(LAST_SELECTED_TEAM_ID_STORAGE_KEY, resolvedTeamHomeTeamId);
    navigate(`${Path.TEAM}/${resolvedTeamHomeTeamId}/${TEAM_DEFAULT_DETAIL_SECTION}`, {
      replace: true,
    });
  }, [
    hasTeams,
    isTeamDetailView,
    isTeamsLoading,
    navigate,
    resolvedTeamHomeTeamId,
    shouldHoldTeamHomeForInvite,
    teamListError,
  ]);

  useEffect(() => {
    const hasSeenTeamBetaIntro = safeLocalStorage.getItem(TEAM_BETA_INTRO_STORAGE_KEY) === 'true';

    if (isTeamInviteRoute || hasSeenTeamBetaIntro || isTeamBetaIntroOpen) {
      return;
    }

    setIsTeamBetaIntroOpen(true);
    trackEventMixpanel('team_beta_intro_shown', {
      source: 'team_page',
    });
  }, [isTeamBetaIntroOpen, isTeamInviteRoute]);

  useEffect(() => {
    if (activeDetailSection !== 'settings') {
      return;
    }

    const sectionId = TEAM_SETTINGS_SECTION_ID_MAP[activeSettingsSection];
    const timeoutId = window.setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (!section) {
        return;
      }

      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeDetailSection, activeSettingsSection]);

  useEffect(() => {
    if (!isTeamDetailView || !teamDetailError || !isTeamNotFoundError(teamDetailError)) {
      return;
    }

    if (activeTeamId) {
      safeLocalStorage.removeItem(LAST_SELECTED_TEAM_ID_STORAGE_KEY);
    }

    navigate(Path.TEAM, { replace: true });
  }, [activeTeamId, isTeamDetailView, teamDetailError, navigate]);

  const closeTeamDropdown = () => {
    setTeamDropdownAnchorEl(null);
    setTeamSearch('');
  };

  const handleToggleTeamDropdown = (event: MouseEvent<HTMLButtonElement>) => {
    setTeamDropdownAnchorEl((anchorEl) => (anchorEl ? null : event.currentTarget));
  };

  const handleSelectTeam = (nextTeamId: string) => {
    closeTeamDropdown();
    setActiveSettingsSection('rename');
    setExcludedResolverTeamId(null);
    safeLocalStorage.setItem(LAST_SELECTED_TEAM_ID_STORAGE_KEY, nextTeamId);
    navigate(`${Path.TEAM}/${nextTeamId}/${TEAM_DEFAULT_DETAIL_SECTION}`);
  };

  const handleNavigateDetailSection = (
    nextSection: TeamDetailSection,
    nextSettingsSection?: TeamSettingsSection
  ) => {
    if (nextSection === 'settings') {
      clearUpdateTeamShareSettingsError();
      clearUpdateTeamError();
      clearLeaveTeamError();
      clearTeamMemberActionError();
      clearTeamProjectActionError();
      setActiveSettingsSection(
        (nextSettingsSection === 'rename' && !canRenameTeam) ||
          (nextSettingsSection === 'projects' && !canManageTeamProjects)
          ? 'sharedCalendar'
          : nextSettingsSection || defaultSettingsSection
      );
    }

    if (activeTeamId) {
      navigate(`${Path.TEAM}/${activeTeamId}/${nextSection}`);
    }
  };

  const handleSaveShareSettings = async (nextValues: {
    sharedCalendarIds: string[];
    sharedTaskBoardIds: string[];
    shareEnergy: boolean;
  }) => {
    if (!activeTeamId) {
      return;
    }

    await updateTeamShareSettings(activeTeamId, nextValues);
    trackTeamEvent('team_share_settings_saved', {
      shared_calendar_count: nextValues.sharedCalendarIds.length,
      shared_task_board_count: nextValues.sharedTaskBoardIds.length,
      share_energy: nextValues.shareEnergy,
    });
  };

  const handleRenameTeam = async (nextTeamName: string) => {
    if (!activeTeamId) {
      return false;
    }

    const updatedTeam = await updateTeamName(activeTeamId, nextTeamName);
    if (updatedTeam) {
      trackTeamEvent('team_renamed');
    }
    return Boolean(updatedTeam);
  };

  const handleUpdateTeamMemberRole = async (memberUserId: string, role: 'admin' | 'member') => {
    if (!activeTeamId) {
      return false;
    }

    const membership = await updateTeamMemberRole(activeTeamId, memberUserId, role);
    if (membership) {
      trackTeamEvent('team_member_role_updated', {
        member_user_id: memberUserId,
        next_role: role,
      });
    }
    return Boolean(membership);
  };

  const handleRemoveTeamMember = async (memberUserId: string) => {
    if (!activeTeamId) {
      return false;
    }

    const membership = await removeTeamMember(activeTeamId, memberUserId);
    if (membership) {
      trackTeamEvent('team_member_removed', {
        member_user_id: memberUserId,
      });
    }
    return Boolean(membership);
  };

  const handleTransferTeamOwnership = async (newOwnerUserId: string) => {
    if (!activeTeamId) {
      return false;
    }

    const didTransferOwnership = await transferTeamOwnership(activeTeamId, newOwnerUserId);
    if (didTransferOwnership) {
      trackTeamEvent('team_ownership_transferred', {
        new_owner_user_id: newOwnerUserId,
      });
    }

    return didTransferOwnership;
  };

  const handleCreateTeamBoard = async (boardName: string) => {
    if (!activeTeamId) {
      return false;
    }

    const createdBoard = await createTeamBoard(activeTeamId, boardName);
    if (createdBoard) {
      trackTeamEvent('team_project_created');
    }

    return createdBoard;
  };

  const handleUpdateTeamBoard = async (boardId: string, boardName: string) => {
    if (!activeTeamId) {
      return false;
    }

    const updatedBoard = await updateTeamBoard(activeTeamId, boardId, boardName);
    if (updatedBoard) {
      trackTeamEvent('team_project_renamed', {
        board_id: boardId,
      });
    }

    return updatedBoard;
  };

  const handleDeleteTeamBoard = async (boardId: string) => {
    if (!activeTeamId) {
      return false;
    }

    const deletedBoard = await deleteTeamBoard(activeTeamId, boardId);
    if (deletedBoard) {
      trackTeamEvent('team_project_deleted', {
        board_id: boardId,
      });
    }

    return deletedBoard;
  };

  const handleLeaveTeam = async () => {
    if (!activeTeamId) {
      return false;
    }

    const didLeaveTeam = await leaveTeam(activeTeamId);
    if (!didLeaveTeam) {
      return false;
    }

    setActiveSettingsSection('rename');
    setExcludedResolverTeamId(activeTeamId);
    safeLocalStorage.removeItem(LAST_SELECTED_TEAM_ID_STORAGE_KEY);
    trackTeamEvent('team_left');
    navigate(Path.TEAM);
    return true;
  };

  const handleDeleteTeam = async () => {
    if (!activeTeamId) {
      return false;
    }

    const didDeleteTeam = await deleteTeam(activeTeamId);
    if (!didDeleteTeam) {
      return false;
    }

    setActiveSettingsSection('rename');
    setExcludedResolverTeamId(activeTeamId);
    safeLocalStorage.removeItem(LAST_SELECTED_TEAM_ID_STORAGE_KEY);
    trackTeamEvent('team_deleted');
    navigate(Path.TEAM);
    return true;
  };

  const handleOpenCreateTeamModal = () => {
    closeTeamDropdown();
    trackEventMixpanel('team_create_modal_opened', {
      source: 'team_switcher',
    });
    openCreateTeamModal();
  };

  const handleOpenInviteModal = () => {
    openCreateTeamInviteModal();
  };

  const handleContinueTeamBetaIntro = () => {
    safeLocalStorage.setItem(TEAM_BETA_INTRO_STORAGE_KEY, 'true');
    trackEventMixpanel('team_beta_intro_continue_clicked', {
      source: 'team_page',
    });
    setIsTeamBetaIntroOpen(false);
  };

  const topChrome = (
    <TeamTopBar
      teamInitials={teamInitials}
      resolvedTeamName={resolvedTeamName}
      activeTeamRole={activeTeamRole}
      activeTeamId={activeTeamId}
      isTeamDropdownOpen={isTeamDropdownOpen}
      teamDropdownAnchorEl={teamDropdownAnchorEl}
      teamSearch={teamSearch}
      filteredTeams={filteredTeams}
      onToggleTeamDropdown={handleToggleTeamDropdown}
      onCloseTeamDropdown={closeTeamDropdown}
      onTeamSearchChange={setTeamSearch}
      onSelectTeam={handleSelectTeam}
      onOpenCreateTeam={handleOpenCreateTeamModal}
    />
  );

  const sideNav = (
    <TeamSideNav
      activeSection={activeDetailSection}
      activeSettingsSection={activeSettingsSection}
      sharedCalendarCount={sharedCalendarCount}
      sharedTaskBoardCount={sharedTaskBoardCount}
      shareEnergyEnabled={shareEnergyEnabled}
      canRenameTeam={canRenameTeam}
      canManageTeamProjects={canManageTeamProjects}
      onNavigate={handleNavigateDetailSection}
    />
  );

  const renderLoadingState = () => {
    return (
      <TeamBlankStage stageKey="team-loading" isLoading>
        <div className="team-page__loading-state">
          <CircularProgress size={28} className="team-page__spinner" />
          <Typography variant="h2" component="h1" className="team-page__title">
            {t('team.page.loadingTeams')}
          </Typography>
        </div>
      </TeamBlankStage>
    );
  };

  const renderErrorState = (message: string) => {
    const content = (
      <TeamErrorStateCard
        message={message}
        showBackToTeams={isTeamDetailView}
        onBackToTeams={() => navigate(Path.TEAM)}
      />
    );

    return isTeamDetailView ? (
      <TeamShell topChrome={topChrome} sideNav={sideNav}>
        <main className="team-shell__state-main">{content}</main>
      </TeamShell>
    ) : (
      <TeamBlankStage stageKey="team-error">{content}</TeamBlankStage>
    );
  };

  const renderCreateTeam = () => {
    return (
      <TeamBlankStage stageKey="team-create">
        <TeamCreateCard
          inputId="team-name"
          teamName={teamName}
          createError={createError}
          isCreateDisabled={isCreateDisabled}
          isCreatingTeam={isCreatingTeam}
          onTeamNameChange={handleTeamNameChange}
          onSubmit={handleCreateTeam}
        />
      </TeamBlankStage>
    );
  };

  const renderCreateTeamModal = () => {
    return (
      <Dialog
        open={isCreateTeamModalOpen}
        onClose={closeCreateTeamModal}
        className="team-create-dialog"
        maxWidth={false}
      >
        <div className="team-create-dialog-content">
          <TeamCreateCard
            inputId="team-name-dialog"
            teamName={teamName}
            createError={createError}
            isCreateDisabled={isCreateDisabled}
            isCreatingTeam={isCreatingTeam}
            onTeamNameChange={handleTeamNameChange}
            onSubmit={handleCreateTeam}
          />
        </div>
      </Dialog>
    );
  };

  const renderCreateTeamInviteModal = () => {
    return (
      <TeamInviteModal
        open={isCreateTeamInviteModalOpen}
        teamName={resolvedTeamName}
        inviteEmail={inviteEmail}
        inviteError={createTeamInviteErrorMessage}
        generatedInviteUrl={generatedInviteUrl}
        isGeneratingInviteLink={isCreatingTeamInvite}
        isCopyingInviteLink={isCopyingInviteLink}
        isInviteLinkCopied={isInviteLinkCopied}
        isGenerateDisabled={isGenerateInviteDisabled}
        onInviteEmailChange={handleInviteEmailChange}
        onGenerateInviteLink={handleGenerateInviteLink}
        onCopyInviteLink={handleCopyInviteLink}
        onClose={closeCreateTeamInviteModal}
      />
    );
  };

  const renderAcceptInviteModal = () => {
    return (
      <TeamAcceptInviteModal
        open={isAcceptInviteModalOpen}
        teamName={inviteTeamName}
        inviteRole={resolvedInviteRole}
        inviterName={resolvedInviteInviterName}
        previewMembers={resolvedInvitePreviewMembers}
        memberSummaryText={resolvedInviteMemberSummaryText}
        isViewerEligible={isViewerEligibleForInvite}
        blockedReason={inviteBlockedReason}
        calendarOptions={inviteCalendarOptions}
        taskBoardOptions={inviteTaskBoardOptions}
        selectedCalendarIds={normalizedInviteCalendarIds}
        selectedTaskBoardIds={normalizedInviteTaskBoardIds}
        shareEnergy={isInviteShareEnergyEnabled}
        isLoadingInvite={isResolvingTeamInvite}
        isLoadingCalendars={isCalendarsLoading}
        isLoadingTaskBoards={isTaskBoardsLoading}
        isAcceptingInvite={isAcceptingTeamInvite}
        inviteError={teamInviteResolveError}
        acceptError={acceptTeamInviteError}
        onToggleCalendar={handleToggleInviteCalendar}
        onToggleTaskBoard={handleToggleInviteTaskBoard}
        onToggleShareEnergy={handleToggleInviteEnergy}
        onAccept={handleAcceptInvite}
        onDecline={handleDeclineInvite}
      />
    );
  };

  const renderContent = () => {
    if (isTeamDetailView) {
      if (isTeamLoading) {
        return renderLoadingState();
      }

      if (teamDetailError) {
        return renderErrorState(teamDetailError);
      }

      if (!selectedTeam) {
        return renderErrorState(t('team.page.teamNotFoundForAccount'));
      }

      return (
        <TeamShell topChrome={topChrome} sideNav={sideNav}>
          {activeDetailSection === 'settings' ? (
            activeTeamId ? (
              <TeamSharingSettings
                teamId={activeTeamId}
                teamName={resolvedTeamName}
                teamRole={activeTeamRole}
                teamOwnerUserId={selectedTeam.ownerUserId}
                members={teamMembers}
                boards={selectedBoards}
                sharedCalendarIds={resolvedSharedCalendarIds}
                sharedTaskBoardIds={resolvedSharedTaskBoardIds}
                shareEnergy={shareEnergyEnabled}
                isSavingShareSettings={isUpdatingTeamShareSettings}
                shareSettingsError={updateTeamShareSettingsError}
                isRenamingTeam={isUpdatingTeam}
                renameTeamError={updateTeamError}
                isUpdatingMemberRole={isUpdatingTeamMemberRole}
                isRemovingMember={isRemovingTeamMember}
                isTransferringOwnership={isTransferringTeamOwnership}
                memberActionError={teamMemberActionError}
                isCreatingTeamBoard={isCreatingTeamBoard}
                isUpdatingTeamBoard={isUpdatingTeamBoard}
                isDeletingTeamBoard={isDeletingTeamBoard}
                teamProjectActionError={teamProjectActionError}
                isLeavingTeam={isTeamExitActionLoading}
                leaveTeamError={leaveTeamError}
                onSaveShareSettings={handleSaveShareSettings}
                onRenameTeam={handleRenameTeam}
                onUpdateMemberRole={handleUpdateTeamMemberRole}
                onRemoveMember={handleRemoveTeamMember}
                onTransferTeamOwnership={handleTransferTeamOwnership}
                onCreateTeamBoard={handleCreateTeamBoard}
                onUpdateTeamBoard={handleUpdateTeamBoard}
                onDeleteTeamBoard={handleDeleteTeamBoard}
                onLeaveTeam={handleLeaveTeam}
                onDeleteTeam={handleDeleteTeam}
                onOpenInviteModal={handleOpenInviteModal}
              />
            ) : (
              <TeamEmptyHome
                teamInitials={teamInitials}
                resolvedTeamName={resolvedTeamName}
                onOpenInviteModal={handleOpenInviteModal}
              />
            )
          ) : activeDetailSection === 'tasks' && activeTeamId ? (
            <TeamTasksView
              teamId={activeTeamId}
              members={teamMembers}
              boards={selectedBoards}
              currentUserId={userId}
            />
          ) : activeTeamId ? (
            <TeamDayView
              teamId={activeTeamId}
              members={teamMembers}
              currentUserId={userId}
              onOpenInviteModal={handleOpenInviteModal}
            />
          ) : (
            <TeamEmptyHome
              teamInitials={teamInitials}
              resolvedTeamName={resolvedTeamName}
              onOpenInviteModal={handleOpenInviteModal}
            />
          )}
        </TeamShell>
      );
    }

    if (isTeamsLoading) {
      return renderLoadingState();
    }

    if (teamListError) {
      return renderErrorState(teamListError);
    }

    if (hasTeams) {
      return renderLoadingState();
    }

    return renderCreateTeam();
  };

  return (
    <div className="team-page">
      <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
      {renderCreateTeamModal()}
      {renderCreateTeamInviteModal()}
      {renderAcceptInviteModal()}
      <TeamBetaIntroModal open={isTeamBetaIntroOpen} onContinue={handleContinueTeamBetaIntro} />
      <AISuggestedEventSheetWrapper />
      <CalendarEventSnackBar />
    </div>
  );
};

export default TeamPage;
